import React from 'react';
import { useSelector } from 'react-redux';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

// Selectors
import { userLanguagePreferenceSelector } from '../../../../../user/currentUserSelector';
import { ActiveTableSelector, useActiveTableSelector } from '../../../../../element/table/store/hotTableStoreHooks';
import { selectActiveTableCellSelection } from '../../../../../element/table/store/selectors/activeHotTableSelectors';
import createSwitchableTableComponent from './createSwitchableTableComponent';
import LegacyTableCellAlignmentTool from './legacy/LegacyTableCellAlignmentTool';

// Components
import ToolbarPopupTool from '../../ToolbarPopupTool';
import Icon from '../../../../../components/icons/Icon';
import TableCellAlignmentToolbarPopup from './TableCellAlignmentToolbarPopup';

// Utils
import { getAllCellsBetween } from '../../../../../element/table/utils/selectionUtils/tableSelectionGeometryUtils';
import { getCellTextAlignment } from '../../../../../element/table/utils/dataUtils/cell/tableCellDisplayUtils';

// Constants
import { PopupIds, PopupSections } from '../../../../../components/popupPanel/popupConstants';
import { TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT } from '../../../../../../common/table/tableConstants';

/**
 * Determines the text alignment that is common across all selected cells.
 * If there's no common alignment, returns undefined.
 */
const selectCommonTextAlignment: ActiveTableSelector<string | undefined, { locale: string }> = (
    activeTable,
    { locale },
) => {
    if (!activeTable) return;

    const currentCellSelections = selectActiveTableCellSelection(activeTable);
    const hotInstance = activeTable.instance;

    if (!currentCellSelections || currentCellSelections.length === 0) return;

    const allCells = getAllCellsBetween(currentCellSelections);
    const firstCell = allCells[0];
    const { cellData } = hotInstance.getCellMeta(firstCell.row, firstCell.col);
    const displayValue = hotInstance.getDataAtCell(firstCell.row, firstCell.col); // For formulas, will return formula result

    const firstTextAlignment = getCellTextAlignment(cellData, locale, displayValue);

    if (!firstTextAlignment) return;

    const hasDifferentTextAlignments = allCells.some(({ row, col }) => {
        const { cellData } = hotInstance.getCellMeta(row, col);
        const displayValue = hotInstance.getDataAtCell(row, col); // For formulas, will return formula result

        const textAlignment = getCellTextAlignment(cellData, locale, displayValue);
        return textAlignment !== firstTextAlignment;
    });

    if (hasDifferentTextAlignments) return;

    return firstTextAlignment;
};

/**
 * Determines the vertical alignment that is common across all selected cells.
 * If there's no common alignment, returns the default vertical alignment.
 */
const selectCommonVerticalAlignment: ActiveTableSelector<string | undefined> = (activeTable) => {
    if (!activeTable) return;

    const currentCellSelections = selectActiveTableCellSelection(activeTable);
    const hotInstance = activeTable.instance;

    if (!currentCellSelections || currentCellSelections.length === 0) return;

    const allCells = getAllCellsBetween(currentCellSelections);
    const { cellData: firstCellData } = hotInstance.getCellMeta(allCells[0].row, allCells[0].col);
    const firstVerticalAlignment = firstCellData?.verticalAlignment;

    if (!firstVerticalAlignment) return TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT;

    const hasDifferentVerticalAlignments = allCells.some(({ row, col }) => {
        const { cellData } = hotInstance.getCellMeta(row, col);
        return cellData?.verticalAlignment !== firstCellData?.verticalAlignment;
    });

    if (hasDifferentVerticalAlignments) return TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT;

    return firstVerticalAlignment;
};

const selectIsDisabled: ActiveTableSelector<boolean> = (activeTable) => {
    const selection = selectActiveTableCellSelection(activeTable);
    return !selection || selection.length === 0;
};

type TableCellAlignmentToolProps = Record<string, unknown> & {
    element?: ImMNElement;
};

const TableCellAlignmentTool: React.FC<TableCellAlignmentToolProps> = (props) => {
    const locale = useSelector(userLanguagePreferenceSelector);

    // Get common text alignment of all selected cells
    const commonTextAlignment = useActiveTableSelector((activeTable) =>
        selectCommonTextAlignment(activeTable, { locale }),
    );

    // Get common vertical alignment of all selected cells
    const commonVerticalAlignment = useActiveTableSelector(selectCommonVerticalAlignment);
    const isDisabled = useActiveTableSelector(selectIsDisabled);

    return (
        <ToolbarPopupTool
            {...props}
            name="Alignment"
            popupId={PopupIds.TABLE_CELL_ALIGNMENT_TOOL}
            closeSection={PopupSections.TOOLBAR}
            toolName={'CellAlignment'}
            disabled={isDisabled}
            buttonSelector=".CellalignmentTool .icon"
            PopupComponent={TableCellAlignmentToolbarPopup}
            preventFocus
            // @ts-ignore Struggling with the JSX type inference
            active={false}
            commonTextAlignment={commonTextAlignment}
            commonVerticalAlignment={commonVerticalAlignment}
        >
            <Icon name={`toolbar-align-${commonTextAlignment?.toLowerCase() || 'justified'}`} />
        </ToolbarPopupTool>
    );
};

export default createSwitchableTableComponent<TableCellAlignmentToolProps>(
    LegacyTableCellAlignmentTool,
    TableCellAlignmentTool,
);
